import { QueryProps } from "@/api/api";

import { useQuery, UseQueryResult, useSuspenseQuery } from "@tanstack/react-query";
import * as React from "react";
import { useEffect } from "react";

import { ApiPath } from "@/api/path";
import { RefreshInterval } from "@/dateRanges/refreshIntervals";
import { LineChartResponse, SeriesDatum } from "@/components/charts/lineChartResponse";
import { DatumCard, DatumCardProps } from "@/components/datum-card";
import { lineCharts } from "@/components/charts/api";

interface DatumCardQueryProps extends DatumCardProps {
  apiPath: ApiPath | string;
  query: QueryProps;
  onQueryResponseUpdate?: (res: UseQueryResult<LineChartResponse | any>) => void;
  compareWithFn?: (_: any) => undefined | { name: string; value: any };
  suffixFn?: (res: any) => string;
  accessorFn?: Function;
  refreshInterval?: RefreshInterval;
  forceLoading?: boolean;
}

export const useDatumCardSuspenseQuery = (
  apiPath: ApiPath,
  query: QueryProps,
  accessorFn: (res: LineChartResponse) => SeriesDatum,
  refreshInterval: RefreshInterval
) => {
  const result = useSuspenseQuery({
    ...lineCharts.detail(apiPath, query),
    refetchInterval: () => {
      if (query.dateRange.value.live && refreshInterval?.value !== 0) {
        return refreshInterval?.value;
      }
      return false;
    },
  });

  return accessorFn(result.data).datum
}

export const DatumCardQuery = ({
                            hideIfNoData = true,
                            hideIfZero = false,
                            ...props
                          }: DatumCardQueryProps) => {
  const result = useQuery({
    ...lineCharts.detail(props.apiPath, props.query),
    refetchInterval: () => {
      if (props.query.dateRange.value.live && props.refreshInterval?.value !== 0) {
        return props.refreshInterval?.value;
      }
      return false;
    },
  });

  useEffect(() => {
    if (props.onQueryResponseUpdate) {
      props.onQueryResponseUpdate(result)
    }
  }, [result]);

  const parsedResult = React.useMemo(() => {
    if (props.accessorFn) {
      return props.accessorFn(result.data);
    } else return result.data;
  }, [result]);

  const diff = React.useMemo(() => {
    if (props.compareWithFn) {
      if (!parsedResult?.datum) return;
      const res = props.compareWithFn(result.data);
      if (res) {
        const percentageDifference = ((parsedResult.datum - res.value) / res.value) * 100;
        return { value: percentageDifference, name: res.name };
      }
    }
  }, [result]);

  const suffix = React.useMemo(() => {
    if (typeof props.suffixFn === "function")
      return props.suffixFn(result.data) || "";
    if (props.suffixFn) return props.suffixFn;
    if (parsedResult?.suffix) return parsedResult?.suffix;
    return "";
  }, [props.query, result, props.suffixFn]);

  return (
    <DatumCard
      datum={parsedResult?.datum}
      suffix={suffix}
      forceLoading={result.isLoading}
      diff={diff}
      {...props}
    />
  );
};
